<template>
  <div :data-id="panelId" class="flex flex-col">
    <div
      :data-id="panelId"
      class="sticky top-0 z-10 flex flex-col bg-pure-white"
    >
      <div :data-id="panelId" class="py-6 px-6 flex flex-col gap-2">
        <div :data-id="panelId" class="flex items-center justify-between gap-4">
          <BlTextTitle4 :data-id="panelId" text="Scheduled sessions" />
          <BlIcon icon="close" :clickable="true" />
        </div>
      </div>

      <div :data-id="panelId" class="px-6">
        <BlTextBody2 :data-id="panelId" text="Showing" class="text-slate-700" />
        <BlTextBody2
          :data-id="panelId"
          :text="totalCount"
          class="text-slate-700"
        />
        <BlTextBody2
          :data-id="panelId"
          text="scheduled session(s)"
          class="text-slate-700"
        />
      </div>
    </div>

    <div
      :data-id="panelId"
      v-if="fetchLoading"
      class="h-128 grid place-items-center"
    >
      <div :data-id="panelId" class="blum-loader"></div>
    </div>
    <div
      :data-id="panelId"
      v-else-if="!scheduledSessions.length"
      class="px-16 h-128 grid place-items-center"
    >
      <BlTextBody1
        :data-id="panelId"
        text="All sessions scheduled for this client will appear here."
        class="text-center"
      />
    </div>
    <div :data-id="panelId" v-else class="px-6 flex flex-col">
      <div
        :data-id="panelId"
        v-for="scheduled in scheduledSessions"
        :key="scheduled.id"
        class="py-3 flex flex-col gap-2 border-b border-slate-400"
      >
        <div :data-id="panelId" class="flex flex-col gap-1 truncate">
          <BlTextSubtitle3
            :data-id="panelId"
            :text="`Session ID ${scheduled.session.id}`"
          />
          <div :data-id="panelId" v-if="scheduled.user" class="truncate">
            <BlTextBody2 :data-id="panelId" text="With" />
            <BlTextBody2
              :data-id="panelId"
              :title="scheduled.user.name || 'deleted user'"
              :text="scheduled.user.name || 'deleted user'"
              class="truncate"
              :class="{ ' italic': !scheduled.user.name }"
            />
          </div>
          <div :data-id="panelId" v-if="scheduled.date">
            <BlTextBody2 :data-id="panelId" text="Scheduled for" />
            <BlTextBody2
              :data-id="panelId"
              :text="
                displayDate({ date: scheduled.date, format: 'ddd DD MMM YYYY' })
              "
            />
          </div>
          <BlTextBody2
            :data-id="panelId"
            v-if="scheduled.credit_transaction_service"
            :title="scheduled.credit_transaction_service.package_item_name"
            :text="scheduled.credit_transaction_service.package_item_name"
            class="truncate"
          />
          <BlTextBody2
            :data-id="panelId"
            v-if="!scheduled.appointment_credits.length"
            :text="`${scheduled.total_credit} credit(s)`"
          />
          <div :data-id="panelId" v-else class="flex flex-col gap-1 truncate">
            <div :data-id="panelId">
              <BlTextBody2
                :data-id="panelId"
                :text="`${scheduled.total_credit} credit(s) of`"
              />
              <BlTextBody2
                :data-id="panelId"
                :text="
                  `${scheduled.appointment_credits[0].credit_transaction_service.package_item.name}:`
                "
              />
            </div>
            <div
              :data-id="panelId"
              v-for="credit in scheduled.appointment_credits"
              :key="credit.id"
              class="px-2 flex items-center gap-2"
            >
              <div
                :data-id="panelId"
                class="h-1 w-1 flex-shrink-0 bg-slate-800 rounded"
              ></div>
              <div :data-id="panelId">
                <BlTextBody2
                  :data-id="panelId"
                  :text="
                    `ID ${credit.credit_transaction_service.credit_transaction_id}:`
                  "
                />
                <BlTextBody2
                  :data-id="panelId"
                  :text="`${credit.session_credit || 0} credit(s)`"
                />
              </div>
            </div>
          </div>
        </div>
        <router-link
          v-if="currentUser.role.includes('admin')"
          :to="`/appointments/details/${scheduled.id}`"
          target="_blank"
        >
          <BlInputButton
            type="outlined"
            dense
            title="View appointment details"
            label="View details"
            class="w-full"
          />
        </router-link>
      </div>
    </div>

    <div
      :data-id="panelId"
      v-if="totalCount > 25"
      class="px-6 flex justify-center mt-4 mb-36"
    >
      <BlInputPagination
        :dataId="panelId"
        :page="page"
        :count="Math.ceil(totalCount / perPage)"
        @changed="changePage"
      />
    </div>
    <div :data-id="panelId" v-else class="mb-46"></div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { displayDate } from "@/plugins/helper.js";

import BlTextTitle4 from "../../BlTextTitle4.vue";
import BlTextSubtitle3 from "../../BlTextSubtitle3.vue";
import BlTextBody1 from "../../BlTextBody1.vue";
import BlTextBody2 from "../../BlTextBody2.vue";
import BlIcon from "../../BlIcon.vue";
import BlInputButton from "../../BlInputButton.vue";
import BlInputPagination from "../../BlInputPagination.vue";

export default {
  name: "ScheduledSessionsPanel",
  components: {
    BlTextTitle4,
    BlTextSubtitle3,
    BlTextBody1,
    BlTextBody2,
    BlIcon,
    BlInputButton,
    BlInputPagination
  },
  props: {
    panelId: { type: String }
  },
  data() {
    return {
      fetchLoading: true,
      scheduledSessions: [],
      totalCount: [],
      page: 1,
      perPage: 25
    };
  },
  computed: {
    ...mapGetters(["currentUser", "currentCenter", "client"])
  },
  mounted() {
    this.fetch();
  },
  methods: {
    ...mapActions(["actionGet"]),
    displayDate,
    async fetch(withoutPagination = false) {
      const params = {
        url: `/api/v1/clients/${this.client.id}/scheduled_sessions`
      };
      if (!withoutPagination) {
        params.url += `?page=${this.page}&per_page=${this.perPage}`;
      }

      this.scheduledSessions = [];
      this.totalCount = 0;

      this.fetchLoading = true;
      const { success, data } = await this.actionGet(params);
      this.fetchLoading = false;
      if (!success) {
        this.fetch(true);
        return;
      }

      this.scheduledSessions = data.scheduled_sessions;
      this.totalCount = data.total_count;
    },
    changePage(page) {
      if (page === this.page) return;
      this.page = page;
      this.fetch();
    },
    changePerPage(perPage) {
      if (perPage === this.perPage) return;
      this.page = 1;
      this.perPage = perPage;
      this.fetch();
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/clients/creditTransaction/ScheduledSessionsPanel.vue